import { useEffect, useMemo, useRef, useState } from 'react';
import { AiFillWarning } from 'react-icons/ai';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import GalleryService from '@/services/gallery.service';
import FormError from '@/shared-components/error/components/FormError';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import ArtworkMediaInput from './components/ArtworkMediaInput';
import ArtworkMultipleInput from './components/ArtworkMultipleInput';
import RequestLimitedEdition from './components/RequestLimitedEdition';
import { getAboutInfoDescriptions } from './constants';
import SearchArtist from './search-artist';

import './index.css';

const DEFAULT_OFFER_SETTINGS = {
  enabled: false,
  maxDiscountPct: 0,
};

const OFFER_DISCOUNT_OPTIONS = [
  { value: 'none', label: 'No Offers' },
  { value: 'default', label: 'Default Offer' },
  { value: 'custom', label: 'Other' },
];

const normalizeOfferMode = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'none' || normalized === 'custom' ? normalized : 'default';
};

const parseOfferMaxDiscount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function About({
  formState,
  setFormState,
  editMode,
  previewMode,
  formError,
  setFormError,
  permissions = {},
  galleryPermissionCheck = null,
  priceFieldKey,
  isAuctionAccount,
}) {
  const t = useTranslations('common.forms.add_artwork');
  const hasArtistPerms = editMode && Object.keys(permissions).length > 0;
  const isGalleryMember = typeof galleryPermissionCheck === 'function';
  const { state } = useStateHandler();
  const session = state?.user_session;
  const isArtistAccount = session?.user_type === 'artist' || session?.user_type === 'gallery_artist';
  const ownArtist = useMemo(() => {
    if (!session) return null;
    if (session.gallery_artist) return session.gallery_artist;
    const fallbackName = session.user_name || session.user_profile_name || session.user_username || '';
    if (!fallbackName) return null;
    return {
      _id: session._id,
      artist_name: fallbackName,
      user_name: session.user_name,
      user_username: session.user_username,
    };
  }, [session]);
  const [isMobile, setIsMobile] = useState(false);
  const [availableGalleries, setAvailableGalleries] = useState([]);
  const [galleriesLoaded, setGalleriesLoaded] = useState(false);
  const [offerSettings, setOfferSettings] = useState(DEFAULT_OFFER_SETTINGS);

  const canGallery = key => (!isGalleryMember ? true : galleryPermissionCheck(key));
  const canEditType = hasArtistPerms ? false : (isArtistAccount ? false : canGallery('artworks.edit_type'));
  const canEditArtist = hasArtistPerms ? false : (isArtistAccount ? false : canGallery('artworks.edit_artist'));
  const canEditTitle = hasArtistPerms ? !!permissions.titleDescription : canGallery('artworks.edit_title');
  const canEditDescription = hasArtistPerms ? !!permissions.titleDescription : canGallery('artworks.edit_description');
  const canEditMedium = hasArtistPerms ? !!permissions.medium : canGallery('artworks.edit_medium');
  const canEditYear = hasArtistPerms ? !!permissions.yearOfCreation : canGallery('artworks.edit_year');
  const canEditColors = hasArtistPerms ? !!permissions.categories : canGallery('artworks.edit_colors');
  const canEditThemes = hasArtistPerms ? !!permissions.categories : canGallery('artworks.edit_themes');
  const canEditFeelings = hasArtistPerms ? !!permissions.categories : canGallery('artworks.edit_feelings');
  const canEditSize = hasArtistPerms ? !!permissions.dimensions : canGallery('artworks.edit_size');
  const canEditPrice = hasArtistPerms ? !!permissions.price : canGallery('artworks.edit_price');
  const canEditCurrency = hasArtistPerms ? false : canEditPrice;
  const canRequestLimitedEditions = hasArtistPerms
    ? !!permissions.requestLimitedEditions
    : canGallery('artworks.request_limited_editions');
  const canEditCertificate = hasArtistPerms ? !!permissions.frameCertificate : canGallery('artworks.edit_certificate');
  const canEditFrame = hasArtistPerms ? !!permissions.frameCertificate : canGallery('artworks.edit_frame');
  const [showDeniedPopup, setShowDeniedPopup] = useState(false);
  const [showActionRejectedPopup, setShowActionRejectedPopup] = useState(false);
  const formRef = useRef(null);
  const resolveFormError = (element) => (
    Array.isArray(formError) ? formError.find((err) => err.element === element) : null
  );
  const typeError = resolveFormError('artwork_type');
  const missingFields = useMemo(() => {
    if (!Array.isArray(formError)) return [];
    const labels = new Set();
    let hasSize = false;

    formError.forEach(err => {
      if (err?.error_code !== 'EMPTY_FIELD') return;
      switch (err.element) {
      case 'artwork_artist':
        labels.add('Artist');
        break;
      case 'artwork_title':
        labels.add('Title');
        break;
      case 'artwork_description':
        labels.add('Description');
        break;
      case 'artwork_medium':
        labels.add('Medium');
        break;
      case 'artwork_year':
        labels.add('Year');
        break;
      case 'artwork_color':
        labels.add('Color');
        break;
      case 'artwork_theme':
        labels.add('Theme');
        break;
      case 'artwork_feeling':
        labels.add('Feeling');
        break;
      case 'artwork_length':
      case 'artwork_height':
        hasSize = true;
        break;
      case 'artwork_currency':
        labels.add('Currency');
        break;
      case priceFieldKey:
      case 'price_starting_at':
        labels.add('Price');
        break;
      default:
        break;
      }
    });

    if (hasSize) labels.add('Size');
    labels.delete('Type');

    return Array.from(labels);
  }, [formError, priceFieldKey]);
  const hasMultipleMissingFields = missingFields.length
    + (typeError?.error_code === 'EMPTY_FIELD' ? 1 : 0) > 1;

  const formatMissingFields = (fields) => {
    if (!fields.length) return '';
    if (fields.length === 1) return fields[0];
    if (fields.length === 2) return `${fields[0]} and ${fields[1]}`;
    return `${fields.slice(0, -1).join(', ')} and ${fields[fields.length - 1]}`;
  };
  const artworkSize = formState?.artwork_about?.artwork_size ?? {
    unit: 'cm',
    height: '',
    length: '',
  };

  const isReadOnlyDesc = previewMode || !canEditDescription;
  const canEditOfferDiscount = hasArtistPerms ? false : canEditPrice;
  const isArtistFieldLocked = isArtistAccount || (editMode && !canEditArtist);
  const isGalleryFieldLocked = isArtistAccount && availableGalleries.length <= 1;
  const selectedGalleryId = useMemo(() => {
    const explicitGallery = formState?.artwork_about?.artwork_gallery;
    if (typeof explicitGallery === 'string' && explicitGallery.trim()) {
      return explicitGallery.trim();
    }
    if (explicitGallery && typeof explicitGallery === 'object' && explicitGallery._id) {
      return explicitGallery._id;
    }
    return session?.user_type === 'gallery' ? session?._id ?? null : null;
  }, [formState?.artwork_about?.artwork_gallery, session?._id, session?.user_type]);
  const artworkOfferSettings = formState?.artwork_about?.artwork_offer_settings ?? {
    mode: 'default',
    max_discount_pct: null,
  };
  const offerMode = normalizeOfferMode(artworkOfferSettings?.mode);
  const customOfferMaxDiscount = parseOfferMaxDiscount(artworkOfferSettings?.max_discount_pct);
  const triggerDeniedPopup = () => {
    if (isGalleryMember) {
      setShowActionRejectedPopup(true);
      return;
    }
    setShowDeniedPopup(true);
  };

  useEffect(() => {
    const node = formRef.current;
    if (!node) return;

    const handleClick = e => {

      /* 0️⃣  Controles siempre bloqueados para el artista. Limited Editions
       * is intentionally excluded: it has its own artist permission flag. */
      if (hasArtistPerms && e.target.closest(`
       .add-artwork-about-form__group-margin
       .search-artist                                 /* buscador de artista      */
   `)) {
        e.preventDefault();
        triggerDeniedPopup();
        return;
      }

      /* ① Click en el icono de info → lo ignoramos */
      if (e.target.closest('.step-accordeon-step-header__info-icon')) return;

      const limitedEditionDisabled = e.target.closest('.request-limited-edition__container.disabled');
      if (limitedEditionDisabled && !canRequestLimitedEditions) {
        e.preventDefault();
        triggerDeniedPopup();
        return;
      }

      /* ② ¿Dentro de algo marcado .is-disabled?      */
      const disabledWrapper = e.target.closest('.is-disabled');
      if (!disabledWrapper) return;

      /* ③ Si sólo fue el wrapper vacío, lo saltamos  */
      if (e.target === disabledWrapper) return;

      /* ④ Todo lo demás ⇒ pop-up                     */
      e.preventDefault();
      triggerDeniedPopup();
    };

    /* fase CAPTURA para interceptar antes que React */
    node.addEventListener('click', handleClick, true);
    return () => node.removeEventListener('click', handleClick, true);
  }, [hasArtistPerms, isGalleryMember, canRequestLimitedEditions]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 769);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  function getPreviewStyles() {
    const { length, height } = artworkSize;
    if (!length || !height) return {};
    const ratio = length / height;
    return ratio <= 1
      ? { aspectRatio: ratio, height: '100%' }
      : { aspectRatio: ratio, width: '100%' };
  }

  const onInputAboutData = (key, value) => {
    setFormState(prev => Utils.addAttributeToObject(`artwork_about.${key}`, value, prev));
    setFormError(prev => {
      if (!prev) return [];
      let elem = key;
      if (key === 'artwork_size.height') elem = 'artwork_height';
      if (key === 'artwork_size.length') elem = 'artwork_length';
      return prev.filter(err => err.element !== elem);
    });
  };

  useEffect(() => {
    if (!isArtistAccount) return;
    let isActive = true;

    (async () => {
      try {
        const { response, data } = await fetchWrapper.get(
          `${config.apis.api.url}/permissions/artist`,
          { injectToken: true }
        );
        if (!isActive) return;
        if (!response.ok) {
          setAvailableGalleries([]);
          setGalleriesLoaded(true);
          return;
        }

        const options = (data ?? []).map((doc) => {
          const galleryDoc = doc?.gallery ?? null;
          const galleryId = typeof galleryDoc === 'string' ? galleryDoc : galleryDoc?._id;
          if (!galleryId) return null;
          const label = galleryDoc?.user_profile_name
            || galleryDoc?.user_name
            || galleryDoc?.user_username
            || galleryId;
          return { value: galleryId, label };
        }).filter(Boolean);

        setAvailableGalleries(options);
        setGalleriesLoaded(true);
      } catch (error) {
        if (isActive) {
          setAvailableGalleries([]);
          setGalleriesLoaded(true);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [isArtistAccount]);

  useEffect(() => {
    if (!isArtistAccount || !ownArtist) return;
    const currentArtist = formState?.artwork_about?.artwork_artist;
    const currentId = currentArtist?._id || currentArtist?.id || null;
    const ownId = ownArtist?._id || ownArtist?.id || null;
    const currentName = currentArtist?.artist_name || currentArtist?.user_name || currentArtist?.user_username || '';
    const ownName = ownArtist?.artist_name || ownArtist?.user_name || ownArtist?.user_username || '';

    if ((ownId && currentId && String(ownId) === String(currentId)) || (!ownId && ownName && ownName === currentName)) {
      return;
    }

    onInputAboutData('artwork_artist', ownArtist);
  }, [formState?.artwork_about?.artwork_artist, isArtistAccount, ownArtist]);

  useEffect(() => {
    if (!isArtistAccount) return;
    const currentType = formState?.artwork_about?.artwork_type;
    if (currentType === 'Work of Artist') return;
    onInputAboutData('artwork_type', 'Work of Artist');
  }, [formState?.artwork_about?.artwork_type, isArtistAccount]);

  useEffect(() => {
    if (!isArtistAccount || !galleriesLoaded) return;
    const currentGallery = formState?.artwork_about?.artwork_gallery ?? null;
    if (availableGalleries.length === 0) {
      if (currentGallery) {
        onInputAboutData('artwork_gallery', null);
      }
      return;
    }
    if (availableGalleries.length === 1) {
      const onlyGalleryId = availableGalleries[0].value;
      if (String(currentGallery) !== String(onlyGalleryId)) {
        onInputAboutData('artwork_gallery', onlyGalleryId);
      }
      return;
    }
    const isValid = availableGalleries.some(
      (option) => String(option.value) === String(currentGallery)
    );
    if (!isValid && currentGallery) {
      onInputAboutData('artwork_gallery', null);
    }
  }, [availableGalleries, formState?.artwork_about?.artwork_gallery, isArtistAccount]);

  useEffect(() => {
    if (!selectedGalleryId) {
      setOfferSettings(DEFAULT_OFFER_SETTINGS);
      return;
    }

    let active = true;
    (async () => {
      try {
        const { response, data } = await GalleryService.getGalleryOfferSettings(selectedGalleryId);
        if (!active) return;
        if (!response.ok) {
          setOfferSettings(DEFAULT_OFFER_SETTINGS);
          return;
        }

        const settings = data?.settings ?? {};
        const maxDiscountRaw = settings?.max_discount_pct ?? settings?.maxDiscountPct ?? 0;
        const parsedMaxDiscount = Number(maxDiscountRaw);
        setOfferSettings({
          enabled: settings?.enabled === true,
          maxDiscountPct: Number.isFinite(parsedMaxDiscount) ? Math.max(0, Math.min(30, parsedMaxDiscount)) : 0,
        });
      } catch (_error) {
        if (active) {
          setOfferSettings(DEFAULT_OFFER_SETTINGS);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [selectedGalleryId]);

  useEffect(() => {
    if (!offerSettings.enabled || offerMode !== 'custom') return;
    if (customOfferMaxDiscount === null) return;
    if (customOfferMaxDiscount <= offerSettings.maxDiscountPct) return;
    onInputAboutData('artwork_offer_settings.max_discount_pct', offerSettings.maxDiscountPct);
  }, [customOfferMaxDiscount, offerMode, offerSettings.enabled, offerSettings.maxDiscountPct]);

  const desc = useMemo(() => getAboutInfoDescriptions(t), [t]);

  return (
    <div ref={formRef} className={`add-artwork-about-form ${previewMode ? 'read-only' : ''}`}>
      {/* Tipo de obra */}
      <PublicFormInput
        className={`add-artwork-about-form__type-of-artwork${!canEditType ? ' is-disabled' : ''}${typeError ? ' type-missing-field' : ''}`}
        type="choose"
        optionAText={desc.find(i => i.name === 'Work of Artist').name}
        optionBText={desc.find(i => i.name === 'Gallery Owned').name}
        optionA={desc.find(i => i.name === 'Work of Artist').value}
        optionB={desc.find(i => i.name === 'Gallery Owned').value}
        value={formState.artwork_about.artwork_type}
        onChange={v => canEditType && onInputAboutData('artwork_type', v)}
        headerInfoIcon={desc.find(i => i.name === 'Work of Artist').header}
        infoIconDescription={desc.find(i => i.name === 'Work of Artist').description}
        headerInfoIcon2={desc.find(i => i.name === 'Gallery Owned').header}
        infoIconDescription2={desc.find(i => i.name === 'Gallery Owned').description}
        readOnly={!canEditType}
      />
      {isArtistAccount && availableGalleries.length > 0 && (
        <PublicFormInput
          className={`add-artwork-about-form__gallery${isGalleryFieldLocked ? ' is-disabled' : ''}`}
          type="select"
          placeholder="Gallery"
          element="artwork_gallery"
          value={formState.artwork_about.artwork_gallery}
          onChange={value => !isGalleryFieldLocked && onInputAboutData('artwork_gallery', value)}
          selectOptions={{ options: availableGalleries, searchable: availableGalleries.length > 4 }}
          error={resolveFormError('artwork_gallery')}
          readOnly={isGalleryFieldLocked}
          margin
        />
      )}
      {/* Artista */}
      <SearchArtist
        className={isArtistFieldLocked ? 'is-disabled' : ''}
        element="artwork_artist"
        value={formState.artwork_about.artwork_artist}
        typeOfArtwork={formState.artwork_about.artwork_type}
        onChange={v => canEditArtist && onInputAboutData('artwork_artist', v)}
        infoIcon={desc.find(i => i.name === 'Artist')}
        error={resolveFormError('artwork_artist')}
        disabled={isArtistFieldLocked}
      />
      {/* Título */}
      <PublicFormInput
        className={`add-artwork-about-form__title${!canEditTitle ? ' is-disabled' : ''}`}
        placeholder="Title"
        element="artwork_title"
        value={formState.artwork_about.artwork_title}
        onChange={e => canEditTitle && onInputAboutData('artwork_title', e.target.value)}
        error={resolveFormError('artwork_title')}
        margin
        infoIconDescription={desc.find(i => i.name === 'Title').description}
        infoIconHeader={desc.find(i => i.name === 'Title').header}
        readOnly={!canEditTitle}
      />
      {/* Descripción */}
      <PublicFormInput
        className={`add-artwork-about-form__description${!canEditDescription ? ' is-disabled' : ''}`}
        placeholder="Description"
        type="textarea"
        element="artwork_description"
        value={formState.artwork_about.artwork_description}
        onChange={e => canEditDescription && onInputAboutData('artwork_description', e.target.value)}
        error={resolveFormError('artwork_description')}
        margin
        infoIconDescription={desc.find(i => i.name === 'Description').description}
        infoIconHeader={desc.find(i => i.name === 'Description').header}
        readOnly={isReadOnlyDesc}         // ← ahora sí cubre los dos casos
        disabled={isReadOnlyDesc}
      />
      {!offerSettings.enabled ? (
        <PublicFormInput
          className="add-artwork-about-form__offers-discount"
          placeholder="Auto Offers Discount"
          element="artwork_offer_discount_disabled"
          value="Not Active"
          infoIconDescription={t('about_info.auto_offers.description')}
          infoIconHeader={t('about_info.auto_offers.title')}
          margin
          readOnly
        />
      ) : (
        <div className="add-artwork-about-form__group offers-discount">
          <PublicFormInput
            className={`add-artwork-about-form__offers-discount${!canEditOfferDiscount ? ' is-disabled' : ''}`}
            placeholder="Auto Offers Discount"
            type="select"
            element="artwork_offer_settings_mode"
            selectOptions={{ options: OFFER_DISCOUNT_OPTIONS }}
            value={offerMode}
            onChange={(value) => {
              if (!canEditOfferDiscount) return;
              const nextMode = normalizeOfferMode(value);
              onInputAboutData('artwork_offer_settings.mode', nextMode);
              if (nextMode !== 'custom') {
                onInputAboutData('artwork_offer_settings.max_discount_pct', null);
              }
            }}
            infoIconDescription={t('about_info.auto_offers.description')}
            infoIconHeader={t('about_info.auto_offers.title')}
            margin
            readOnly={!canEditOfferDiscount}
          />
          {offerMode === 'custom' && (
            <PublicFormInput
              className={`add-artwork-about-form__offers-discount-max${!canEditOfferDiscount ? ' is-disabled' : ''}`}
              placeholder={`Percentage (max ${offerSettings.maxDiscountPct}%)`}
              element="artwork_offer_settings_max_discount_pct"
              type="number"
              min="0"
              max={offerSettings.maxDiscountPct}
              step="0.1"
              inputMode="decimal"
              value={customOfferMaxDiscount ?? ''}
              onChange={e => {
                if (!canEditOfferDiscount) return;
                const nextValue = e.target.value;
                if (!/^\d*([.]\d*)?$/.test(nextValue)) return;
                const parsedValue = nextValue === '' ? null : Number(nextValue);
                if (parsedValue !== null && Number.isFinite(parsedValue) && parsedValue > offerSettings.maxDiscountPct) {
                  onInputAboutData('artwork_offer_settings.max_discount_pct', offerSettings.maxDiscountPct);
                  return;
                }
                onInputAboutData('artwork_offer_settings.max_discount_pct', nextValue === '' ? null : nextValue);
              }}
              margin
              readOnly={!canEditOfferDiscount}
            />
          )}
        </div>
      )}
      {/* Medium + Year */}
      <div className="add-artwork-about-form__group select">
        <ArtworkMediaInput
          className={!canEditMedium ? 'is-disabled' : ''}
          element="artwork_medium"
          previewMode={previewMode}
          value={formState.artwork_about.artwork_medium}
          onChange={v => canEditMedium && onInputAboutData('artwork_medium', v)}
          error={resolveFormError('artwork_medium')}
          infoIcon={desc.find(i => i.name === 'Medium')}
          margin
          disabled={!canEditMedium}
        />

        <PublicFormInput
          className={`add-artwork-about-form__yearOfDescription${!canEditYear ? ' is-disabled' : ''}`}
          placeholder="Year of Creation"
          element="artwork_year"
          value={formState.artwork_about.artwork_year}
          onChange={e => canEditYear && onInputAboutData('artwork_year', e.target.value)}
          error={resolveFormError('artwork_year')}
          infoIconDescription={desc.find(i => i.name === 'Year of Creation').description}
          infoIconHeader={desc.find(i => i.name === 'Year of Creation').header}
          readOnly={!canEditYear}
        />
      </div>
      {/* Color / Theme / Emotion */}
      <div className="add-artwork-about-form__group">
        <div className="add-artwork-about-form__group-medium variant">
          <ArtworkMultipleInput
            className={!canEditColors ? 'is-disabled' : ''}
            placeholder="Color(s)"
            type="colors"
            options={config.forms.color_options}
            category="colors"
            value={formState.artwork_about.artwork_color}
            element="artwork_color"
            onChange={v => canEditColors && onInputAboutData('artwork_color', v)}
            error={resolveFormError('artwork_color')}
            previewMode={previewMode}
            margin
            infoIcon={desc.find(i => i.name === 'Color')}
            disabled={!canEditColors}
          />

          <ArtworkMultipleInput
            className={!canEditThemes ? 'is-disabled' : ''}
            placeholder="Theme(s)"
            type="themes"
            options={config.forms.theme_options}
            category="themes"
            value={formState.artwork_about.artwork_theme}
            element="artwork_theme"
            onChange={v => canEditThemes && onInputAboutData('artwork_theme', v)}
            error={resolveFormError('artwork_theme')}
            previewMode={previewMode}
            margin
            infoIcon={desc.find(i => i.name === 'Theme')}
            disabled={!canEditThemes}
          />

          <ArtworkMultipleInput
            className={!canEditFeelings ? 'is-disabled' : ''}
            placeholder="Emotion(s)"
            type="emotions"
            options={config.forms.feeling_options}
            category="feelings"
            value={formState.artwork_about.artwork_feeling}
            element="artwork_feeling"
            onChange={v => canEditFeelings && onInputAboutData('artwork_feeling', v)}
            error={resolveFormError('artwork_feeling')}
            previewMode={previewMode}
            margin
            infoIcon={desc.find(i => i.name === 'Emotion')}
            disabled={!canEditFeelings}
          />

          {/* Currency + Price */}
          <div className="add-artwork-about-form__group currency-year">
            <PublicFormInput
              className={`add-artwork-about-form__group-smaller variant add-artwork-about-form__group-margin${!canEditCurrency ? ' is-disabled' : ''}`}
              placeholder="Currency"
              type="select"
              element="artwork_currency"
              selectOptions={{ options: config.forms.currency_options }}
              value={formState.artwork_about.artwork_currency}
              onChange={v => canEditCurrency && onInputAboutData('artwork_currency', v)}
              error={resolveFormError('artwork_currency')}
              margin
            />

            {/* The curated auction account specifies the starting bid instead of the fixed price. */}
            <PublicFormInput
              className={`add-artwork-about-form__group-bigger variant${!canEditPrice ? ' is-disabled' : ''}`}
              placeholder={isAuctionAccount ? "Starting price (excluding taxes)" : "Price (excluding taxes)"}
              element={priceFieldKey}
              value={formState.artwork_about[priceFieldKey]}
              onChange={e => canEditPrice && /^[0-9]*$/.test(e.target.value) && onInputAboutData(priceFieldKey, e.target.value)}
              error={resolveFormError(priceFieldKey)}
              infoIconDescription={desc.find(i => i.name === 'Price').description}
              infoIconHeader={desc.find(i => i.name === 'Price').header}
              margin
              readOnly={!canEditPrice}
            />
          </div>
        </div>

        {/* Dimensions */}
        <div className="add-artwork-about-form__group-outter">
          <div>
            <div className="add-artwork-about-form__group center" id="artwork_size">
              <PublicFormInput
                className={`add-artwork-about-form__choose-input variant${!canEditSize ? ' is-disabled' : ''}`}
                type="choose"
                optionA="cm"
                optionB="in"
                value={artworkSize.unit}
                onChange={v => canEditSize && onInputAboutData('artwork_size.unit', v)}
                headerInfoIcon={desc.find(i => i.name === 'Dimensions').header}
                infoIconDescription={desc.find(i => i.name === 'Dimensions').description}
                readOnly={!canEditSize}
              />

              <PublicFormInput
                element="artwork_length"
                type="number"
                placeholder={`Length (${artworkSize.unit})`}
                className={`add-artwork-about-form-dimenssions variant${!canEditSize ? ' is-disabled' : ''}`}
                value={artworkSize.length}
                onChange={e => canEditSize && onInputAboutData('artwork_size.length', e.target.value)}
                error={resolveFormError('artwork_length')}
                readOnly={!canEditSize}
              />

              {!isMobile && <Text className="add-artwork-about-form__size-separator">x</Text>}

              <PublicFormInput
                element="artwork_height"
                type="number"
                placeholder={`Height (${artworkSize.unit})`}
                className={`add-artwork-about-form-dimenssions${!canEditSize ? ' is-disabled' : ''}`}
                value={artworkSize.height}
                onChange={e => canEditSize && onInputAboutData('artwork_size.height', e.target.value)}
                error={resolveFormError('artwork_height')}
                readOnly={!canEditSize}
              />
            </div>
            <FormError error={formError} element="artwork_size" />
          </div>

          <div className="square-start">
            <div className={
              Array.isArray(formError) &&
              (resolveFormError('artwork_length') ||
               resolveFormError('artwork_height'))
                ? 'square-container-error'
                : 'square-container'
            }>
              <div className="square" style={getPreviewStyles()} />
            </div>
          </div>
        </div>
      </div>
      {/* ─── Limited Editions ─── */}
      {(!editMode || !formState.artwork_about.limitedEditionsAccepted) && (
        <RequestLimitedEdition
          previewMode={previewMode}
          unit={artworkSize.unit}
          height={artworkSize.height}
          length={artworkSize.length}
          price={formState.artwork_about[priceFieldKey]}
          typeOfArtwork={formState.artwork_about.artwork_type}
          value={formState.artwork_about.artwork_limited_edition}
          onChange={v => {
            onInputAboutData('artwork_limited_edition', v);
            // si no tengo permiso o no estoy en editMode, salgo
            if (!editMode || !canRequestLimitedEditions) return;

            // disparo petición sólo al pasar a true
            if (v) {
              GalleryService.requestLimitedEditions(formState._id, {
                unit: artworkSize.unit,
                height: artworkSize.height,
                length: artworkSize.length,
                price: formState.artwork_about[priceFieldKey],
              });
            }
          }}
          infoIcon={desc.find(i => i.name === 'Limited Editions')}
          approved={Boolean(Object.keys(formState.artwork_limited_editions || {}).length)}
          disabled={!canRequestLimitedEditions}
        />
      )}
      {/* Checkboxes */}
      <PublicFormInput
        element="artwork_includes_certificate"
        type="checkbox"
        className={`add-artwork-about-form__checkbox-margin${!canEditCertificate ? ' is-disabled' : ''}`}
        value={formState.artwork_about.artwork_includes_certificate}
        onChange={v => canEditCertificate && onInputAboutData('artwork_includes_certificate', v)}
        readOnly={!canEditCertificate}
      >
        {t('checkboxes.certificate_included')}
      </PublicFormInput>
      <PublicFormInput
        element="artwork_includes_frame"
        type="checkbox"
        className={`${!canEditFrame ? ' is-disabled' : ''}`}
        value={formState.artwork_about.artwork_includes_frame}
        onChange={v => canEditFrame && onInputAboutData('artwork_includes_frame', v)}
        readOnly={!canEditFrame}
      >
        {t('checkboxes.frame_included')}
      </PublicFormInput>
      {hasMultipleMissingFields && (
        <div className="form-error__message-container active">
          <AiFillWarning className="form-error__message-icon" />
          <p className="form-error__message-text">
            Complete all fields missing
          </p>
        </div>
      )}
      {!hasMultipleMissingFields && typeError && (
        <div className="form-error__message-container active">
          <AiFillWarning className="form-error__message-icon" />
          <p className="form-error__message-text">
            Missing the type of artwork field, select either Work of Artist or Gallery Owned
          </p>
        </div>
      )}
      {!hasMultipleMissingFields && !typeError && missingFields.length > 0 && (
        <div className="form-error__message-container active">
          <AiFillWarning className="form-error__message-icon" />
          <p className="form-error__message-text">
            {`Fields missing: ${formatMissingFields(missingFields)}`}
          </p>
        </div>
      )}
      <GenericPopup
        open={showDeniedPopup}
        onClick={() => setShowDeniedPopup(false)}   /* clic en el backdrop = cerrar */
        opacity                                     /* <<< mismo efecto que los info-popups */
        className="generic-popup_content"           /* tarjeta con los estilos estándar */
      >
        <div className="generic-popup__header">
          <XIcon
            className="generic-popup__close"
            onClick={() => setShowDeniedPopup(false)}
          />
          <Text className="generic-popup__header-text-theme">
      Field locked
          </Text>
        </div>

        <p>Edits to this field are managed by the gallery. Please get in touch with your gallery representative to obtain editing permissions.</p>
      </GenericPopup>
      <ActionRejectedPopup
        open={showActionRejectedPopup}
        close={() => setShowActionRejectedPopup(false)}
        description="You do not have permission to edit this field. Please contact your gallery admin."
      />
    </div>
  );
}
